/* eslint-disable @typescript-eslint/naming-convention */
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { HotToastService } from '@ngneat/hot-toast';
import { TimezonePickerService } from 'app/core/services/timezone/timezone-picker.service';
import { ProfileSettingsService } from './profile-settings.service';
import { Subscription, timer } from 'rxjs';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { ZuperDialogService } from '@zuper/components/zuper-dialog';
import { UserService } from 'app/core/user/user.service';
import { MatDialogRef } from '@angular/material/dialog';
import { countriesIso, CountryIso } from '../data-picker/quick-create-customer/quick-create-customer.interface';
import { FileSaverService } from 'ngx-filesaver';
import { take } from 'rxjs/operators';

type ProfileContainer = 'User Profile' | 'Security' | 'Change Password' | 'MFA Setup' | 'Authenticator' | 'SMS' | 'EMAIL' | 'Recovery Codes' | 'OTP';

type AUTH_MODES = 'EMAIL' | 'SMS' | 'TOTP';
@Component({
  selector: 'app-profile-settings',
  templateUrl: './profile-settings.component.html',
  styleUrls: ['./profile-settings.component.scss']
})
export class ProfileSettingsComponent implements OnInit, OnDestroy {

  @ViewChild('imageInput') imageInput: ElementRef;

  public editProfileSettings: boolean = false;
  public profileContainerId: ProfileContainer = 'User Profile';

  public user: any | null = null;
  public userPreferences: any | null = null;

  public fullUsername: string = '';
  public selectedTab: string = 'profile';
  public userPreferedLanguage: string = '';
  public disableUpdateButton: boolean = false;

  public timeFormatList: any[] = [
    { label: '12 Hour Format (Eg: 07:00 PM)', value: '12_HRS' },
    { label: '24 Hour Format (Eg: 19:00 Hrs)', value: '24_HRS' },
  ];

  public dateFormatList: any[] = [
    { label: 'DD/MM/YYYY (Eg: 12/03/2018)', value: 'DD/MM/YYYY' },
    { label: 'MM/DD/YYYY (Eg: 03/12/2018)', value: 'MM/DD/YYYY' },
    { label: 'YYYY/MM/DD (Eg: 2018/03/12)', value: 'YYYY/MM/DD' },
  ];

  public supportedLanguages: any[] = [
    { label: 'English', value: 'en' },
    { label: 'Italian', value: 'it' },
    { label: 'Spanish', value: 'es' },
    { label: 'French', value: 'fr' },
    { label: 'German', value: 'de' },
    { label: 'Portuguese', value: 'pt' },
    { label: 'Portuguese (Brazilian)', value: 'pt-br' },
    { label: 'Dutch', value: 'nl' },
    { label: 'Russian', value: 'ru' },
    { label: 'Indonesian (Bahasa)', value: 'id' },
    { label: 'French (Canada)', value: 'fr-CA' }
  ];

  public timezoneList: any[] = [];

  public userProfileForm: FormGroup = new FormGroup({
    first_name: new FormControl('', Validators.required),
    last_name: new FormControl('', Validators.required),
    email: new FormControl('', { validators: [Validators.required, Validators.email] }),
    designation: new FormControl('', Validators.required),
    mobile_phone_number: new FormControl(''),
    work_phone_number: new FormControl(''),
    profile_picture: new FormControl('')
  });

  public userPreferencesForm: FormGroup = new FormGroup({
    timezone: new FormControl('', Validators.required),
    language: new FormControl('', Validators.required),
    date_format: new FormControl('', Validators.required),
    time_format: new FormControl('', Validators.required),
  });

  public changePasswordForm: FormGroup = new FormGroup({
    currentPassword: new FormControl('', Validators.required),
    newPassword: new FormControl('', [Validators.required, Validators.minLength(8)]),
    confirmPassword: new FormControl('', [Validators.required, Validators.minLength(8)]),
  }, this.utilityService.ConfirmPasswordValidator('newPassword', 'confirmPassword'));

  public multiFactorAuthSetupDetails: any[] = [];

  public authenticatorAppSetupConfig: any = null;
  otpFormControl: FormControl = new FormControl(null);

  countries: CountryIso[] = countriesIso;
  selectedCountry: CountryIso;
  mobileNumberFormControl: FormControl = new FormControl(null, [Validators.required, Validators.pattern(/^[0-9]*$/)]);

  emailFormControl: FormControl = new FormControl(null, [Validators.required, Validators.email]);

  tick = 1000;
  counter: number = 0;
  resendCodeCount: number = 1;
  countDown: Subscription;

  recoveryCodes: any[] = [];
  recoveryCodeDetails: any = {};

  is2faEnabled: boolean = false;
  selected2faMode: string = '';
  retryOtpResponse: any;
  isOtpSentToUser: boolean = false;

  private userSaveSubscription: Subscription;

  constructor(private timezoneService: TimezonePickerService, private profileSettingsService: ProfileSettingsService,
    private toastService: HotToastService, private utilityService: UtilityService, private zuperDialogService: ZuperDialogService,
    private userService: UserService, public dialogRef: MatDialogRef<ProfileSettingsComponent>,
    private fileSaverService: FileSaverService) { }

  ngOnInit(): void {
    this.userDetailsSaveSubscription();
    this.getSavedUserDetails();
    this.getTimeZoneList();
    this.selectedCountry = this.getCountryCode(this.utilityService.companyCountry);
    this.is2faEnabled = this.utilityService.companyPolicy['2fa_enabled'] ?? false;
    if (this.is2faEnabled) {
      this.getMFASetupDetails();
      this.getRecoveryCodes();
    }
  }

  ngOnDestroy(): void {
    this.countDown?.unsubscribe();
    this.userSaveSubscription?.unsubscribe();
  }

  public openProfileContainer(containerId: ProfileContainer): void {
    this.profileContainerId = containerId;
  }

  // #region User Profile Settings

  public onEditProfileClick(): void {
    this.selectedTab = 'profile';
    this.editProfileSettings = true;
    this.userProfileForm.patchValue(this.user);
    this.userPreferencesForm.patchValue(this.userPreferences);
  }

  public switchTab(tabName: string): void {
    this.selectedTab = tabName;
  }

  public onProfileImageClick(): void {
    this.imageInput.nativeElement.click();
  }

  public onImageSelected(event: any): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (file?.type && ['image/jpg', 'image/jpeg', 'image/png', 'image/gif'].includes(file?.type)) {
        this.utilityService.uploadFile(file).subscribe({
          next: (response: any) => {
            this.user.profile_picture = response?.data?.attachment_path;
            this.userProfileForm.controls['profile_picture'].setValue(response?.data?.attachment_path);
          },
          error: (err: any) => {
            event.target.value = '';
          }
        });
      } else {
        event.target.value = '';
        this.toastService.error(this.utilityService.getLocaleValue('file_format.not_supported.text', 'File format is not supported'));
      }
    }
  }

  public onProfileUpdateClick(): void {
    if (this.userProfileForm.valid && this.userPreferencesForm.valid) {
      this.saveUserProfileSettings();
    }
    else {
      if (this.userProfileForm.invalid) {
        this.selectedTab = 'profile';
        this.userProfileForm.markAllAsTouched();
        this.userProfileForm.updateValueAndValidity();
      }
      if (this.userPreferencesForm.invalid) {
        this.selectedTab = 'preferences';
        this.userPreferencesForm.markAllAsTouched();
        this.userPreferencesForm.updateValueAndValidity();
      }
    }
  }

  /**
   * Save User Profile Settings
   */
  saveUserProfileSettings(): void {
    const requestBody: any = {
      user: {
        user_uid: this.user.user_uid,
        first_name: this.userProfileForm.controls['first_name'].value,
        last_name: this.userProfileForm.controls['last_name'].value,
        email: this.userProfileForm.controls['email'].value,
        designation: this.userProfileForm.controls['designation'].value,
        work_phone_number: this.userProfileForm.controls['work_phone_number'].value || null,
        mobile_phone_number: this.userProfileForm.controls['mobile_phone_number'].value || null,
        home_phone_number: this.userProfileForm.controls['work_phone_number'].value || null,
        profile_picture: this.userProfileForm.controls['profile_picture'].value || null,
        timezone: this.userPreferencesForm.controls['timezone'].value,
        date_format: this.userPreferencesForm.controls['date_format'].value,
        time_format: this.userPreferencesForm.controls['time_format'].value,
        language: this.userPreferencesForm.controls['language'].value
      }
    };
    this.disableUpdateButton = true;
    this.profileSettingsService.updateUserPreferences(this.user.user_uid, requestBody).subscribe({
      next: (response: any) => {
        this.profileSettingsService.updateUserDetailsInLocalStorage();
        this.toastService.success(response?.message);
      },
      error: (err: any) => {
        this.disableUpdateButton = false;
      }
    });
  }

  /**
   * Get User details from Local Storage
   */
  getSavedUserDetails(): void {
    const storedUserData: any = this.profileSettingsService.getUserDataFromLocalStorage();
    if (storedUserData) {
      this.user = storedUserData.userDetails;
      this.userPreferences = storedUserData.userPreferences;
      this.userPreferedLanguage = this.supportedLanguages.find(x => x.value === this.userPreferences.language)?.label;
      this.fullUsername = `${this.user.first_name} ${this.user.last_name}`;
    }
  }

  /**
   * Subscription for User Details Save updates
   */
  userDetailsSaveSubscription(): void {
    this.userSaveSubscription = this.profileSettingsService.userDetailsSubject.asObservable().subscribe({
      next: (response: boolean) => {
        if (response && response === true) {
          this.getSavedUserDetails();
          this.editProfileSettings = false;
          this.disableUpdateButton = false;
        }
      }
    });
  }

  public onCancelClick(): void {
    this.disableUpdateButton = false;
    this.editProfileSettings = false;
    this.userProfileForm.reset();
    this.userPreferencesForm.reset();
  }

  // #region Change Password Settings

  // Validate Change Password Form
  onChangePasswordUpdateClick(): void {
    if (this.changePasswordForm.valid) {
      this.saveChangePassword();
    }
    else {
      this.changePasswordForm.markAllAsTouched();
      this.changePasswordForm.updateValueAndValidity();
    }
  }

  /**
   * Save updated password details
   */
  saveChangePassword(): void {
    const requestBody: any = {
      current_password: this.changePasswordForm.controls['currentPassword'].value ?? '',
      new_password: this.changePasswordForm.controls['newPassword'].value ?? '',
      confirm_password: this.changePasswordForm.controls['confirmPassword'].value ?? ''
    };
    this.disableUpdateButton = true;
    this.toastService.loading(`${this.utilityService.getLocaleValue('user.updating.toast.message')}`, { id: 'user_details_update_loader' });
    this.profileSettingsService.saveChangeUserPassword(requestBody).subscribe({
      next: (response: any) => {
        this.toastService.close('user_details_update_loader');
        this.toastService.success(response?.message);
      },
      error: (err: any) => {
        this.toastService.close('user_details_update_loader');
        this.disableUpdateButton = false;
      },
      complete: () => {
        this.changePasswordForm.reset();
        this.profileContainerId = 'Security';
        this.disableUpdateButton = false;
      }
    });
  }

  cancelChangePassword(): void {
    this.disableUpdateButton = false;
    this.changePasswordForm.reset();
  }

  // #region MFA Settings

  getMFASetupDetails(): void {
    this.profileSettingsService.getMultiFactorAuthDetails().subscribe({
      next: (response: any) => {
        this.multiFactorAuthSetupDetails = response?.data;
      }
    });
  }

  //  Authenticator App Setup
  showAuthAppSetup(): void {
    const requestBody: any = {
      mode: 'TOTP'
    };
    this.profileSettingsService.getAuthenticatorAppSetupConfig(requestBody).subscribe({
      next: (response: any) => {
        this.authenticatorAppSetupConfig = response?.totp_details;
      },
      complete:() => {
        this.openProfileContainer('Authenticator');
      }
    });
  }

  cancelAuthAppSetup(): void {
    this.otpFormControl.reset();
    this.disableUpdateButton = false;
    this.openProfileContainer('MFA Setup');
  }

  // EMAIL / SMS Setup
  show2FASetup(mode: 'EMAIL' | 'SMS'): void {
    if (this.check2FAForVerify(mode)) {
      this.selected2faMode = mode;
      this.retryOtpVerification(mode);
      this.openProfileContainer('OTP');
    } else {
      this.openProfileContainer(mode);
    }
  }

  sendSMSOtp(): void {
    if (this.selectedCountry && !this.mobileNumberFormControl.invalid) {
      const requestBody: any = {
        mode: 'SMS',
        phone_number: `${this.selectedCountry.code}-${this.mobileNumberFormControl.value}`
      };
      if (this.resendCodeCount < 5) {
        this.counter = this.resendCodeCount > 2 ? (this.resendCodeCount - 1) * 60 : this.resendCodeCount * 30;
      }
      this.startTimer();
      if (this.resendCodeCount < 5) {
        this.profileSettingsService.getAuthenticatorAppSetupConfig(requestBody).subscribe({
          next: (response: any) => {
            if (response) {
              this.toastService.success(this.utilityService.getLocaleValue('text.mobile_number_otp_info'));
              this.isOtpSentToUser = true;
              this.mobileNumberFormControl.disable();
              this.resendCodeCount++;
            }
          },
          complete: () => {
            this.getMFASetupDetails();
          }
        });
      }
      else {
        this.toastService.error(this.utilityService.getLocaleValue('text.project.job.create_button_disabled'));
      }
    }
    else {
      this.mobileNumberFormControl.markAsTouched();
      this.mobileNumberFormControl.updateValueAndValidity();
    }
  }

  cancelSMSSetup(): void {
    this.resetTimer();
    this.mobileNumberFormControl.enable();
    this.mobileNumberFormControl.reset();
    this.otpFormControl.reset();
    this.isOtpSentToUser = false;
    this.openProfileContainer('MFA Setup');
  }

  // Email Setup
  sendEmailOtp(): void {
    if (!this.emailFormControl.invalid) {
      const requestBody: any = {
        mode: 'EMAIL',
        email: this.emailFormControl.value
      };
      if (this.resendCodeCount < 5) {
        this.counter = this.resendCodeCount > 2 ? (this.resendCodeCount - 1) * 60 : this.resendCodeCount * 30;
      }
      this.startTimer();
      if (this.resendCodeCount < 5) {
        this.profileSettingsService.getAuthenticatorAppSetupConfig(requestBody).subscribe({
          next: (response: any) => {
            if (response) {
              this.toastService.success(this.utilityService.getLocaleValue('text.email_otp_info'));
              this.isOtpSentToUser = true;
              this.emailFormControl.disable();
              this.resendCodeCount++;
            }
          }, complete: () => {
            this.getMFASetupDetails();
          }
        });
      }
      else {
        this.toastService.error(this.utilityService.getLocaleValue('text.project.job.create_button_disabled'));
      }
    }
    else {
      this.mobileNumberFormControl.markAsTouched();
      this.mobileNumberFormControl.updateValueAndValidity();
    }
  }

  cancelEmailSetup(): void {
    this.resetTimer();
    this.emailFormControl.enable();
    this.emailFormControl.reset();
    this.otpFormControl.reset();
    this.isOtpSentToUser = false;
    this.openProfileContainer('MFA Setup');
  }

  /**
   * Retry OTP Verification
   */
  retryOtpVerification(mode: 'SMS' | 'EMAIL'): void {
    const requestBody: any = {
      mode: mode,
    };
    if (this.resendCodeCount < 5) {
      this.counter = this.resendCodeCount > 2 ? (this.resendCodeCount - 1) * 60 : this.resendCodeCount * 30;
    }
    this.startTimer();
    if (this.resendCodeCount < 5) {
      this.profileSettingsService.retryOtpVerification(requestBody).subscribe({
        next: (response: any) => {
          this.retryOtpResponse = response;
          this.isOtpSentToUser = true;
          this.resendCodeCount++;
          this.toastService.success(response?.message);
        },
      });
    }
    else {
      this.toastService.error(this.utilityService.getLocaleValue('text.project.job.create_button_disabled'));
    }
  }

  cancelVerifyOTP(): void {
    this.resetTimer();
    this.otpFormControl.reset();
    this.isOtpSentToUser = false;
    this.openProfileContainer('MFA Setup');
  }

  /**
   * Verify OTP for the selected mode
   */
  verifyOtp(mode: AUTH_MODES): void {
    const otpValue: string = this.otpFormControl.value;
    if ([undefined, '', null].includes(otpValue) || otpValue.length !== 6) {
      this.toastService.error(this.utilityService.getLocaleValue('text.otp_verification_alert'));
    }
    else {
      const requestBody: any = {
        mode: mode,
        otp: otpValue
      };
      this.disableUpdateButton = true;
      this.profileSettingsService.verifyAuthenticatorApp(requestBody).subscribe({
        next: (response: any) => {
          this.toastService.success(response?.message);
        },
        error: (err: any) => {
          this.disableUpdateButton = false;
        },
        complete: () => {
          if (mode === 'SMS') { this.cancelSMSSetup(); }
          if (mode === 'EMAIL') { this.cancelEmailSetup(); }
          if (mode === 'TOTP') { this.cancelAuthAppSetup(); }
          this.otpFormControl.reset();
          this.disableUpdateButton = false;
          this.getMFASetupDetails();
        }
      });
    }
  }

  /**
   * Remove active 2FA
   *
   * @param mode
   */
  showRemoveAuthAlert(mode: AUTH_MODES, fromVerifyScreen: boolean = false): void {
    const removeAuthAlert = this.zuperDialogService.open({
      'title': `${this.utilityService.getLocaleValue('text.remove_2fa', 'Remove 2FA mode')}`,
      'type': 'error',
      'message': `${this.utilityService.getLocaleValue('text.remove_2fa_alert_message', 'Are you sure you want to remove this 2FA mode')} '${mode}'?`,
      'actions': {
        'confirm': {
          'show': true,
          'label': 'Remove',
          'color': 'error',
          'confirmDisplayKey': 'text.remove',
        },
        'cancel': {
          'show': true,
          'label': 'Cancel',
          'cancelDisplayKey': 'cancel.text'
        }
      },
      'dismissible': false,
    });
    removeAuthAlert.afterClosed().subscribe((result) => {
      if (result) {
        this.profileSettingsService.remove2FaSetup(mode).subscribe({
          next: (response: any) => {
            this.toastService.success(response?.message);
            this.openProfileContainer('MFA Setup');
          },
          complete: () => {
            if(fromVerifyScreen){
              this.resetTimer();
              this.otpFormControl.reset();
              this.isOtpSentToUser = false;
            }
            this.getMFASetupDetails();
          }
        });
      }
    });
  }

  // Recovery Codes
  showRecoveryCodeSetup(): void {
    if (!this.recoveryCodes.length) {
      this.generateRecoveryCodes();
    }
    this.openProfileContainer('Recovery Codes');
  }

  getRecoveryCodes(): void {
    this.profileSettingsService.getRecoveryCodes().subscribe({
      next: (response: any) => {
        this.recoveryCodes = response?.data ?? [];
      },
      complete: () => {
        if (this.recoveryCodes.length) {
          this.processRecoveryCodes();
        }
      }
    });
  }

  generateRecoveryCodes(): void {
    this.profileSettingsService.generateRecoveryCodes().subscribe({
      next: (response: any) => {
        this.recoveryCodes = response?.data ?? [];
      },
      complete: () => {
        if (this.recoveryCodes.length) {
          this.processRecoveryCodes();
        }
      }
    });
  }

  processRecoveryCodes(): void {
    const usedCodes = this.recoveryCodes.filter(x => x?.is_used).length;
    this.recoveryCodeDetails = {
      usedCodes,
      availableCodes: this.recoveryCodes.length - usedCodes,
      generatedDate: this.recoveryCodes[0]?.created_at
    };
  }

  /**
   * Download Recovery Codes as Text file
   */
  downloadRecoveryCode(): void {
    let content = `${this.utilityService.getLocaleValue('text.2fa.recovery_codes')} \n ${this.utilityService.getLocaleValue('text.2fa.recovery_codes_usage_info')}`;
    for (const i of this.recoveryCodes) {
      if (i?.is_used) {
        content += '\n ' + i.recovery_code + ` (${this.utilityService.getLocaleValue('text.used')})`;
      }
      else {
        content += '\n ' + i.recovery_code;
      }
    }
    const blob = new Blob([content], {
      type: 'text/plain;charset=utf-8',
    });
    this.fileSaverService.save(blob, 'codes.txt');
  }

  /**
   * Print Recovery Codes
   */
  printRecoveryCodes(): void {
    let printContents = `<br><h2>${this.utilityService.getLocaleValue('text.2fa.recovery_codes')}</h2><hr><br>`;
    for (const i of this.recoveryCodes) {
      if (i?.is_used) {
        printContents += `<p>${i.recovery_code} (${this.utilityService.getLocaleValue('text.used')})</p>`;
      }
      else {
        printContents += `<p>${i.recovery_code}</p>`;
      }
    }
    const printWindow: WindowProxy = window.open(' ', '');
    printWindow.document.open();
    printWindow.document.write('<html><head>');
    printWindow.document.write('<style>*{ font-family: \'Segoe UI\', Tahoma, Geneva, Verdana, sans-serif; }</style>');
    printWindow.document.write('</head><body onload="window.print()">');
    printWindow.document.write(printContents);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
  }

  // #region Logout All Sessions

  /**
   * Logout User from all devices
   */
  onUserLogoutClick(): void {
    const logoutAlert = this.zuperDialogService.open({
      'title': `${this.utilityService.getLocaleValue('text.logout_all_sessions', 'Log out of all Sessions')}`,
      'type': 'error',
      'message': `${this.utilityService.getLocaleValue('text.logout_all_sessions_info', 'This will log you out of all devices, including this active one')}`,
      'actions': {
        'confirm': {
          'show': true,
          'label': 'Logout',
          'color': 'error',
          'confirmDisplayKey': 'text.logout',
        },
        'cancel': {
          'show': true,
          'label': 'Cancel',
          'cancelDisplayKey': 'cancel.text'
        }
      },
      'dismissible': false,
    });
    logoutAlert.afterClosed().subscribe((result) => {
      if (result) {
        this.dialogRef.close();
        this.userService.signOut({ logout_all_session: true });
      }
    });
  }

  transform(value: number): string {
    const minutes: number = Math.floor(value / 60);
    return (
      ('00' + minutes).slice(-2) +
      ':' +
      ('00' + Math.floor(value - minutes * 60)).slice(-2)
    );
  }

  show2FAStatus(mode: AUTH_MODES): string {
    const selectedSetup = this.multiFactorAuthSetupDetails.find(x => x.mode === mode);
    if (!selectedSetup) {
      return 'text.enable';
    }
    if (!selectedSetup.is_verified) {
      return mode === 'TOTP' ? 'text.enable' : 'login.auth.app.verify.text';
    }
    return 'enabled.label';
  }

  check2FAForSetup(mode: AUTH_MODES): boolean {
    return this.multiFactorAuthSetupDetails.find(x => x.mode === mode) === undefined;
  }

  check2FAForVerify(mode: AUTH_MODES): boolean {
    return this.multiFactorAuthSetupDetails.find(x => x.mode === mode)?.is_verified === false;
  }

  check2FAForRemove(mode: AUTH_MODES): boolean {
    return this.multiFactorAuthSetupDetails.find(x => x.mode === mode)?.is_verified === true;
  }

  get2FASetupDate(mode: AUTH_MODES): string {
    return this.multiFactorAuthSetupDetails.find(x => x.mode === mode)?.created_at;
  }

  changeCountryCode(country: CountryIso): void {
    this.selectedCountry = country;
  }

  private getTimeZoneList(): void {
    this.timezoneService.timeZones.subscribe({
      next: (response: any) => {
        if (response && response !== null) {
          this.timezoneList = response;
        }
        else {
          this.timezoneList = [];
        }
      }
    });
  }

  private getCountryCode(country: string): CountryIso {
    return this.countries.find(value => value.iso === country || (value.name?.toLowerCase()?.replaceAll(' ', '') === country?.toLowerCase()?.replaceAll(' ', '')));
  }

  private startTimer(): void {
    this.countDown = timer(0, this.tick).pipe(take(this.counter)).subscribe(() => {
      --this.counter;
      if (this.counter === 0) {
        this.countDown.unsubscribe();
      }
    });
  }

  private resetTimer(): void {
    this.counter = 0;
    this.resendCodeCount = 1;
    this.countDown?.unsubscribe();
  }

}
